import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Dialog } from '@headlessui/react';
import { Link, useSearchParams } from 'react-router-dom';
import apiV2 from '../../../services/apiV2';
import { AppShell } from '../../../components/layout/AppShell';
import {
  useTeacherWorkspace,
  useTeacherUnsavedChanges,
} from '../../../components/layout/TeacherWorkspace';
import { teacherV2NavItems } from '../../../components/layout/v2NavItems';
import './PlanningWorkspace.css';
import { LessonSequencePreview } from '../../../components/v2/teacher/LessonSequencePreview';
import { SharedStartingPlan } from '../../../components/v2/teacher/SharedStartingPlan';
import { GuidedPlanning } from '../../../components/v2/teacher/GuidedPlanning';

type Plan = {
  estimated_minutes?: number | null;
  id: number;
  hook_id?: number;
  title: string;
  content_format: string;
  activity_types: string[];
  hook_level?: string;
};
type Suggestion = { plan_id: number; week: number | null; day: number | null };
type Planning = { start_date: string; weeks: number; items: Suggestion[] };
type Unit = {
  starting_plans?: Record<string, any>;
  owner_section_id?: number;
  section_ids?: number[];
  id: number;
  title: string;
  revision: number;
  published: boolean;
  activities: { plan_id: number }[];
  planning?: Partial<Planning>;
  projects?: { hook_id: number; title: string }[];
};
type Meeting = {
  key: string;
  project_key: string;
  title: string;
  items: {
    title: string;
    plan_id: number;
    required?: boolean;
    availability?: string;
  }[];
};
type Catalog = {
  sections?: { id: number; name: string }[];
  section: { id: number; name: string };
  today: string;
  next_school_date?: string;
  plans: Plan[];
  units: Unit[];
  memberships: { id: number; hook_id: number; title: string }[];
  meetings: Meeting[];
};
type SchoolDate = {
  date: string;
  weekday: number;
  is_school_day: boolean;
  is_adjusted_school_friday: boolean;
  closure_label: string;
};
type Item = {
  id: number;
  title: string;
  kind: string;
  pacing: 'teacher' | 'independent';
  plan_id: number | null;
  directions: string;
  url: string;
  minutes: number | null;
  whole_class: boolean;
  student_ids: number[];
  status: string;
  lesson_group?: { key?: string; title?: string; source_plan_id?: number; step_index?: number; step_count?: number; role?: string };
  lesson_outline?: { id: number; title: string; step_kind: string; stage_tag: string; runner_phase: string; minutes: number | null }[];
  lesson_customizable?: boolean;
};
type Day = {
  date: string;
  agenda_enabled: boolean;
  agenda: Item[];
  day: { revision: number } | null;
  students: { id: number; name: string }[];
};
type Draft = {
  kind?: 'lesson' | 'quick' | 'work';
  itemId?: number;
  planId: number;
  title: string;
  directions: string;
  url: string;
  pacing: 'teacher' | 'independent';
  minutes: string;
  wholeClass: boolean;
  studentIds: number[];
  before?: number;
};
type Scope = 'day' | 'week' | 'unit' | 'library';
type HistoryItem = {
  id: string;
  date: string;
  title: string;
  kind: 'lesson' | 'quick' | 'work';
  plan_id: number | null;
  pacing: 'teacher' | 'independent';
  minutes: number | null;
  directions: string;
  url: string;
  status: string;
};
type Drag = {
  kind: 'plan' | 'agenda' | 'history';
  id: number;
  context: string;
};
const mime = 'application/x-elevate-planner';
const fromIso = (value: string) => {
  const [y, m, d] = value.split('-').map(Number);
  return new Date(y, m - 1, d, 12);
};
const toIso = (value: Date) =>
  `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, '0')}-${String(value.getDate()).padStart(2, '0')}`;
const addDays = (value: string, count: number) => {
  const date = fromIso(value);
  date.setDate(date.getDate() + count);
  return toIso(date);
};
const monday = (value: string) =>
  addDays(value, -((fromIso(value).getDay() + 6) % 7));
const pretty = (value: string, options: Intl.DateTimeFormatOptions) =>
  fromIso(value).toLocaleDateString(undefined, options);
const weekNumber = (start: string, value: string) =>
  Math.round(
    (fromIso(monday(value)).getTime() - fromIso(monday(start)).getTime()) /
      604800000,
  ) + 1;
const labels: Record<string, string> = {
  bell_ringer: 'Bell ringer',
  instruction: 'Instruction',
  practice: 'Practice',
  lab: 'Lab',
  extension: 'Go Further',
  alternate_path: 'Try Another Way',
  resource: 'Helpful resource',
  reflection: 'Reflection',
  exit_ticket: 'Exit ticket',
  assessment: 'Assessment',
};
const purpose = (plan: Plan) =>
  plan.activity_types.length === 1
    ? labels[plan.activity_types[0]] || 'Activity'
    : plan.content_format === 'activity'
      ? 'Activity'
      : 'Full lesson';

async function planningCatalog(section: string) {
  const [base, shared] = await Promise.all([
    apiV2.get(`/pathways/sections/${section}/`),
    apiV2.get(`/pathways/sections/${section}/planning/`),
  ]);
  const rows = new Map<number, Unit>((base.data.units || []).map((u: Unit) => [u.id, u]));
  for (const u of shared.data.units || []) rows.set(u.id, { activities: [], projects: [], published: false, ...rows.get(u.id), ...u });
  return { data: { ...base.data, units: Array.from(rows.values()), sections: shared.data.sections || [] } };
}

export function PlanningWorkspace() {
  const workspace = useTeacherWorkspace();
  const [params, setParams] = useSearchParams();
  const section = workspace?.selected || params.get('section') || '';
  const unitId = params.get('unit') || '';
  const plannerView = params.get('workspace') === 'agenda' ? 'agenda' : 'schedule';
  const [catalog, setCatalog] = useState<Catalog | null>(null);
  const [activityGroups, setActivityGroups] = useState<Item | null>(null);
  const [openLessonIds, setOpenLessonIds] = useState<number[]>([]);
  const [day, setDay] = useState<Day | null>(null);
  const [calendar, setCalendar] = useState<SchoolDate[]>([]);
  const [calendarError, setCalendarError] = useState('');
  const [scope, setScope] = useState<Scope>('unit');
  const [source, setSource] = useState<'original' | 'category' | 'history'>(
    'original',
  );
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [historyError, setHistoryError] = useState('');
  const [historyLoading, setHistoryLoading] = useState(false);
  const [query, setQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [projectFilter, setProjectFilter] = useState('connected');
  const [draft, setDraft] = useState<Draft | null>(null);
  const [suggestion, setSuggestion] = useState<{
    plan: Plan;
    where: 'unit' | 'week' | 'day';
  } | null>(null);
  const [setup, setSetup] = useState<{
    create: boolean;
    title: string;
    start: string;
    weeks: number;
    membership: string;
  } | null>(null);
  const [meeting, setMeeting] = useState('');
  const [previewLesson, setPreviewLesson] = useState(false);
  const [periodMinutes, setPeriodMinutes] = useState(40);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [drag, setDrag] = useState<Drag | null>(null);
  const [dropIndex, setDropIndex] = useState<number | null>(null);
  useTeacherUnsavedChanges(!!(draft || suggestion || setup) || busy);
  const pending = useRef<{
    signature: string;
    payload: Record<string, unknown>;
  } | null>(null);
  const locked = useRef(false);
  const unit = catalog?.units.find((row) => String(row.id) === unitId);
  const chosenDate =
    params.get('date') || catalog?.next_school_date || catalog?.today || '';
  const context = `${section}:${unitId}:${chosenDate}`;
  const currentContext = useRef(context);
  currentContext.current = context;
  const dayUrl =
    section && chosenDate
      ? `/pathways/sections/${section}/classroom/?date=${chosenDate}`
      : '';
  const calendarMonday = chosenDate ? monday(chosenDate) : '';
  const selectedSchoolDate = calendar.find((row) => row.date === chosenDate);
  const instructionalDay = !!selectedSchoolDate?.is_school_day;
  const planning = unit?.planning;
  const start = planning?.start_date || '';
  const weeks = planning?.weeks || 3;
  const unitWeek = start && chosenDate ? weekNumber(start, chosenDate) : 0;
  const unitDay = chosenDate ? ((fromIso(chosenDate).getDay() + 6) % 7) + 1 : 0;
  const inUnit =
    !!start && unitWeek > 0 && unitWeek <= weeks && chosenDate >= start;
  const agenda = (day?.agenda || []).filter(
    (item) => item.status !== 'skipped',
  );
  const unitPlanIds = useMemo(
    () =>
      new Set([
        ...(unit?.activities || []).map((a) => a.plan_id),
        ...(planning?.items || []).map((a) => a.plan_id),
      ]),
    [unit, planning],
  );

  useEffect(() => {
    if (!section || section === 'all') return;
    let active = true;
    setCatalog(null);
    setDay(null);
    setError('');
    planningCatalog(section)
      .then(({ data }) => {
        if (active) setCatalog(data);
      })
      .catch(() => {
        if (active)
          setError('Could not load this class. Try Refresh activities.');
      });
    return () => {
      active = false;
    };
  }, [section]);
  useEffect(() => {
    if (source !== 'history' || !section || !chosenDate) return;
    let active = true;
    setHistory([]);
    setHistoryError('');
    setHistoryLoading(true);
    apiV2
      .get(`/pathways/sections/${section}/?history_before=${chosenDate}`)
      .then(({ data }) => {
        if (active) setHistory(data.history || []);
      })
      .catch(() => {
        if (active)
          setHistoryError(
            'Could not load class history. Switch tabs and try again.',
          );
      })
      .finally(() => {
        if (active) setHistoryLoading(false);
      });
    return () => {
      active = false;
    };
  }, [section, chosenDate, source]);
  useEffect(() => {
    setDraft(null);
    setSuggestion(null);
    setSetup(null);
    setError('');
    setNotice('');
    setDrag(null);
    setDropIndex(null);
    pending.current = null;
  }, [context]);
  useEffect(() => {
    if (!dayUrl || !unitId) return;
    let active = true;
    setDay(null);
    apiV2
      .get(dayUrl)
      .then(({ data }) => {
        if (active) setDay(data);
      })
      .catch(() => {
        if (active)
          setError(
            'Could not load this date. Choose the date again or refresh the page.',
          );
      });
    return () => {
      active = false;
    };
  }, [dayUrl, unitId]);
  useEffect(() => {
    if (!section || !calendarMonday || !unitId) return;
    let active = true;
    setCalendar([]);
    setCalendarError('');
    apiV2
      .get(
        `/goals/sections/${section}/school-days/?from_date=${calendarMonday}&to_date=${addDays(calendarMonday, 6)}`,
      )
      .then(({ data }) => {
        if (active) setCalendar(data.school_days);
      })
      .catch(() => {
        if (active)
          setCalendarError(
            'Could not load the school calendar. Refresh before scheduling activities.',
          );
      });
    return () => {
      active = false;
    };
  }, [section, calendarMonday, unitId]);

  const navigateContext = (values: Record<string, string>) => {
    setParams((previous) => {
      const next = new URLSearchParams(previous);
      Object.entries(values).forEach(([key, value]) =>
        value ? next.set(key, value) : next.delete(key),
      );
      return next;
    });
  };
  const navigateWeek = async (value: string) => {
    const at = currentContext.current;
    try {
      const first = monday(value);
      const { data } = await apiV2.get(
        `/goals/sections/${section}/school-days/?from_date=${first}&to_date=${addDays(first, 6)}`,
      );
      const open = data.school_days.find(
        (row: SchoolDate) => row.is_school_day && (!start || row.date >= start),
      );
      if (at === currentContext.current)
        navigateContext({ date: open?.date || value });
    } catch {
      if (at === currentContext.current)
        setCalendarError('Could not load that school week. Please try again.');
    }
  };
  const chooseUnit = (row: Unit) => {
    setSource('original');
    setMeeting('');
    setScope('unit');
    setQuery('');
    setTypeFilter('all');
    setProjectFilter('connected');
    const first = row.planning?.start_date;
    const today = catalog!.next_school_date || catalog!.today;
    const date =
      first &&
      (today < first || weekNumber(first, today) > (row.planning?.weeks || 3))
        ? first
        : today;
    navigateContext({ unit: String(row.id), date });
  };
  const refreshCatalog = async () => {
    const at = currentContext.current;
    try {
      const response = await planningCatalog(section);
      if (at === currentContext.current) {
        setCatalog(response.data);
        setNotice('Activities refreshed.');
        setError('');
      }
    } catch {
      if (at === currentContext.current)
        setError('Could not refresh activities.');
    }
  };
  const refreshShared = async () => {
    const at = context;
    await refreshCatalog();
    if (dayUrl) {
      const result = await apiV2.get(dayUrl);
      if (at === currentContext.current) setDay(result.data);
    }
  };
  const saveUnit = async (
    payload: Record<string, unknown>,
    success: string,
  ) => {
    if (locked.current) return;
    locked.current = true;
    setBusy(true);
    setError('');
    const at = context;
    try {
      const { data } = await apiV2.post(
        `/pathways/sections/${section}/`,
        payload,
      );
      if (at !== currentContext.current) return;
      setCatalog((await planningCatalog(section)).data);
      setSetup(null);
      setSuggestion(null);
      setNotice(success);
      if (data.selected_unit_id) {
        const created = data.units.find(
          (row: Unit) => row.id === data.selected_unit_id,
        );
        if (created) chooseUnit(created);
      }
    } catch (cause: any) {
      if (at !== currentContext.current) return;
      if (cause?.response?.status === 409) await refreshCatalog();
      setError(
        cause?.response?.data?.detail ||
          'Could not save the unit. Your choices are still here.',
      );
    } finally {
      locked.current = false;
      setBusy(false);
    }
  };
  const saveSetup = () => {
    if (!setup) return;
    const value = {
      start_date: setup.start,
      weeks: setup.weeks,
      items: planning?.items || [],
    };
    void saveUnit(
      setup.create
        ? {
            action: 'create_unit',
            title: setup.title,
            membership_id: setup.membership ? Number(setup.membership) : null,
            planning: { ...value, items: [] },
          }
        : {
            action: 'update_planning',
            unit_id: unit!.id,
            revision: unit!.revision,
            planning: value,
          },
      'Unit planning dates saved.',
    );
  };
  const saveSuggestion = (remove = false) => {
    if (!unit || !suggestion || !start) return;
    const items = (planning?.items || []).filter(
      (row) => row.plan_id !== suggestion.plan.id,
    );
    if (!remove)
      items.push({
        plan_id: suggestion.plan.id,
        week: suggestion.where === 'unit' ? null : unitWeek,
        day: suggestion.where === 'day' ? unitDay : null,
      });
    void saveUnit(
      {
        action: 'update_planning',
        unit_id: unit.id,
        revision: unit.revision,
        planning: { start_date: start, weeks, items },
      },
      remove
        ? 'Removed from the planning collection. Saved daily plans are unchanged.'
        : 'Activity saved in this unit. It has not been assigned to students.',
    );
  };
  const available = useMemo(() => {
    if (!catalog || !unit) return [];
    const matchingIds = new Set(
      (planning?.items || [])
        .filter(
          (item) =>
            item.week === unitWeek && (scope !== 'day' || item.day === unitDay),
        )
        .map((item) => item.plan_id),
    );
    const hooks = new Set(
      unit.projects?.map((project) => project.hook_id) || [],
    );
    return catalog.plans.filter((plan) => {
      if (scope === 'library') {
        if (projectFilter === 'unit' && !unitPlanIds.has(plan.id)) return false;
        if (
          projectFilter === 'connected' &&
          hooks.size &&
          !hooks.has(plan.hook_id!)
        )
          return false;
        if (
          projectFilter !== 'all' &&
          projectFilter !== 'connected' &&
          projectFilter !== 'unit' &&
          String(plan.hook_id) !== projectFilter
        )
          return false;
      } else if (
        !unitPlanIds.has(plan.id) ||
        ((scope === 'day' || scope === 'week') &&
          (!inUnit || !matchingIds.has(plan.id)))
      )
        return false;
      return (
        (typeFilter === 'all' || plan.activity_types.includes(typeFilter)) &&
        (!query.trim() ||
          `${plan.title} ${purpose(plan)}`
            .toLowerCase()
            .includes(query.trim().toLowerCase()))
      );
    });
  }, [
    catalog,
    unit,
    planning,
    unitWeek,
    unitDay,
    scope,
    projectFilter,
    unitPlanIds,
    inUnit,
    typeFilter,
    query,
  ]);
  const unitMeetings = (catalog?.meetings || []).filter((recipe) =>
    recipe.items.every((item) => unitPlanIds.has(item.plan_id)),
  );
  const visibleMeetings =
    scope === 'library' ? catalog?.meetings || [] : unitMeetings;
  const selectedMeeting = visibleMeetings.find(
    (row) => `${row.project_key}:${row.key}` === meeting,
  );
  const originalMeetings = catalog?.meetings || [];
  const originalMeeting =
    originalMeetings.find(
      (row) => `${row.project_key}:${row.key}` === meeting,
    ) || (meeting ? undefined : unitMeetings[0]);
  const displayedPlans =
    source === 'original' && originalMeeting
      ? originalMeeting.items
          .map((item) =>
            catalog?.plans.find((plan) => plan.id === item.plan_id),
          )
          .filter((plan): plan is Plan => !!plan)
      : available;
  const optionalItem = (item: Meeting['items'][number]) =>
    item.required === false ||
    ['after_required_work', 'student_choice'].includes(item.availability || '');
  const missingCore =
    originalMeeting?.items.filter(
      (item) =>
        !optionalItem(item) &&
        !agenda.some(
          (row) => row.plan_id === item.plan_id && row.status !== 'skipped',
        ),
    ) || [];
  const collectMeeting = () => {
    if (!selectedMeeting || !unit || !start) return;
    const ids = new Set((planning?.items || []).map((item) => item.plan_id));
    const items = [
      ...(planning?.items || []),
      ...selectedMeeting.items
        .filter((item) => !ids.has(item.plan_id))
        .map((item) => ({
          plan_id: item.plan_id,
          week: inUnit ? unitWeek : null,
          day: inUnit && instructionalDay ? unitDay : null,
        })),
    ];
    void saveUnit(
      {
        action: 'update_planning',
        unit_id: unit.id,
        revision: unit.revision,
        planning: { start_date: start, weeks, items },
      },
      'Meeting activities collected in this unit. Choose which ones to add to the day.',
    );
  };
  const totalMinutes = agenda.reduce(
    (sum, item) => sum + (item.minutes || 0),
    0,
  );
  const missingMinutes = agenda.filter((item) => !item.minutes).length;

  const command = async (
    action: string,
    values: Record<string, unknown>,
    success: string,
  ) => {
    if (
      (action !== 'agenda_enable' && !day?.agenda_enabled) ||
      !instructionalDay ||
      !dayUrl ||
      locked.current
    )
      return;
    const at = context;
    const signature = JSON.stringify({ action, values, context });
    if (pending.current?.signature !== signature)
      pending.current = {
        signature,
        payload: {
          action,
          ...values,
          revision: day?.day?.revision || 0,
          request_id: crypto.randomUUID(),
        },
      };
    locked.current = true;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const response = await apiV2.post(dayUrl, pending.current.payload);
      if (at !== currentContext.current) return;
      pending.current = null;
      setDay(response.data);
      setDraft(null);
      setMeeting('');
      setNotice(success);
    } catch (cause: any) {
      if (at !== currentContext.current) return;
      if (cause?.response?.status >= 400 && cause?.response?.status < 500)
        pending.current = null;
      if (cause?.response?.status === 409) {
        try {
          const response = await apiV2.get(dayUrl);
          if (at === currentContext.current) setDay(response.data);
        } catch {
          /* Retain choices for retry. */
        }
      }
      if (at === currentContext.current)
        setError(
          cause?.response?.data?.detail ||
            'Could not confirm this change. Retry to check and save it safely.',
        );
    } finally {
      locked.current = false;
      setBusy(false);
    }
  };
  const beginAdd = (plan: Plan, before?: number) => {
    void command(
      'agenda_add',
      {
        kind: 'lesson',
        plan_id: plan.id,
        pacing: 'teacher',
        minutes: plan.estimated_minutes ?? null,
        whole_class: true,
        student_ids: [],
        ...(before ? { before_item_id: before } : {}),
      },
      `${plan.title} added for everyone, teacher-led. Use Adjust to change delivery, time, or audience. Nothing is opened for students.`,
    );
  };
  const beginQuick = () => {
    setDraft({
      kind: 'quick',
      planId: 0,
      title: '',
      directions: '',
      url: '',
      pacing: 'teacher',
      minutes: '8',
      wholeClass: true,
      studentIds: [],
    });
  };
  const beginReuse = (item: HistoryItem, before?: number) => {
    void command(
      'agenda_add',
      {
        kind: item.kind,
        title: item.title,
        directions: item.directions,
        url: item.url,
        ...(item.plan_id ? { plan_id: item.plan_id } : {}),
        pacing: item.pacing,
        minutes: item.minutes,
        whole_class: true,
        student_ids: [],
        ...(before ? { before_item_id: before } : {}),
      },
      `${item.title} added as a fresh review for everyone. Use Adjust to change the audience. Previous work is unchanged.`,
    );
  };
  const beginAdjust = (item: Item) => {
    setError('');
    setDraft({
      kind:
        item.kind === 'quick' || item.kind === 'work' ? item.kind : 'lesson',
      itemId: item.id,
      planId: item.plan_id || 0,
      title: item.title,
      directions: item.directions || '',
      url: item.url || '',
      pacing: item.pacing,
      minutes: item.minutes ? String(item.minutes) : '',
      wholeClass: item.whole_class,
      studentIds: item.student_ids || [],
    });
  };
  const saveDraft = () => {
    if (!draft) return;
    const common = {
      pacing: draft.pacing,
      minutes: draft.minutes.trim() ? Number(draft.minutes) : null,
      whole_class: draft.wholeClass,
      student_ids: draft.wholeClass ? [] : draft.studentIds,
    };
    void command(
      draft.itemId ? 'agenda_update' : 'agenda_add',
      draft.itemId
        ? {
            ...common,
            item_id: draft.itemId,
            title: draft.title,
            directions: draft.directions,
            url: draft.url,
          }
        : {
            ...common,
            kind: draft.kind || 'lesson',
            ...(draft.kind === 'quick' || draft.kind === 'work'
              ? {
                  title: draft.title,
                  directions: draft.directions,
                  url: draft.url,
                }
              : { plan_id: draft.planId }),
            ...(draft.before ? { before_item_id: draft.before } : {}),
          },
      `${draft.title} saved in the daily plan.`,
    );
  };
  const reorder = (id: number, before?: number) => {
    if (id === before) return;
    const ids = agenda.map((item) => item.id).filter((value) => value !== id);
    ids.splice(before === undefined ? ids.length : ids.indexOf(before), 0, id);
    if (ids.join() !== agenda.map((item) => item.id).join())
      void command(
        'agenda_reorder',
        {
          item_ids: [
            ...ids,
            ...(day?.agenda || [])
              .filter((item) => item.status === 'skipped')
              .map((item) => item.id),
          ],
        },
        'Activity order saved.',
      );
  };
  const startDrag = (
    event: React.DragEvent,
    kind: Drag['kind'],
    id: number,
  ) => {
    const value = { kind, id, context };
    event.dataTransfer.setData(mime, JSON.stringify(value));
    event.dataTransfer.effectAllowed = kind === 'agenda' ? 'move' : 'copy';
    setDrag(value);
  };
  const drop = (event: React.DragEvent, index: number) => {
    event.preventDefault();
    event.stopPropagation();
    setDrag(null);
    setDropIndex(null);
    if (busy || !instructionalDay) return;
    try {
      const value: Drag = JSON.parse(event.dataTransfer.getData(mime));
      if (value.context !== context) return;
      if (value.kind === 'plan') {
        const plan = catalog?.plans.find((p) => p.id === value.id);
        if (plan) beginAdd(plan, agenda[index]?.id);
      }
      if (value.kind === 'history' && history[value.id])
        beginReuse(history[value.id], agenda[index]?.id);
      if (
        value.kind === 'agenda' &&
        agenda.some((item) => item.id === value.id)
      )
        reorder(value.id, agenda[index]?.id);
    } catch {
      /* Ignore drops from outside this planner. */
    }
  };
  const dropProps = (index: number) => ({
    onDragOver: (event: React.DragEvent) => {
      if (!busy && event.dataTransfer.types.includes(mime)) {
        event.preventDefault();
        event.dataTransfer.dropEffect =
          drag?.kind === 'agenda' ? 'move' : 'copy';
        setDropIndex(index);
      }
    },
    onDrop: (event: React.DragEvent) => drop(event, index),
  });
  const plannerUrl = `/v2/teacher/planning?section=${section}&unit=${unitId}&date=${chosenDate}`;
  const repositoryUrl = (hookId: number) =>
    `/v2/teacher/plan/hooks/${hookId}?section=${section}&repository=1&planner=${encodeURIComponent(plannerUrl)}`;
  const closeDialogs = () => {
    if (!busy) {
      setDraft(null);
      setSetup(null);
      setSuggestion(null);
      setError('');
    }
  };
  const dialogTitle =
    draft?.title ||
    suggestion?.plan.title ||
    (draft
      ? 'Quick add to this day'
      : setup?.create
        ? 'Create a planning unit'
        : 'Unit dates');

  return (
    <AppShell
      role="teacher"
      title="Unit planner"
      navItems={teacherV2NavItems('pathways')}
      className="elevate-planner-shell"
    >
      <div className="elevate-planner space-y-5">
        <header className="ep-heading">
          <div>
            {unit && (
              <button
                type="button"
                className="ep-back"
                disabled={busy}
                onClick={() => navigateContext({ unit: '' })}
              >
                ← My units
              </button>
            )}
            <h1 className="mt-1 text-3xl font-semibold tracking-tight">
              {unit ? 'Plan the learning' : 'Plan your units'}
            </h1>
            <p className="mt-2 text-muted">
              {unit
                ? 'Schedule a lesson, a week, or the complete unit—then adjust each class as needed.'
                : 'Choose a unit to organize its activities, weeks, and daily plans.'}
            </p>
          </div>
          <div className="ep-heading-actions">
            <button
              className="ep-secondary"
              disabled={busy}
              onClick={() => void refreshCatalog()}
            >
              Refresh activities
            </button>
            {unit && (
              <button
                className="ep-secondary"
                disabled={busy}
                onClick={() =>
                  setSetup({
                    create: false,
                    title: unit.title,
                    start: start || chosenDate,
                    weeks,
                    membership: '',
                  })
                }
              >
                Unit dates
              </button>
            )}
          </div>
        </header>
        {error && !draft && !setup && !suggestion && (
          <div className="ep-alert" role="alert">
            {error}
          </div>
        )}
        {notice && (
          <p className="ep-notice" role="status">
            {notice}
          </p>
        )}
        {!section || section === 'all' ? (
          <p>Choose a class to plan its units.</p>
        ) : !catalog ? (
          <p role="status">Loading units…</p>
        ) : !unit ? (
          <>
            <section className="ep-card">
              <h2>A simpler planning flow</h2>
              <p className="mt-2"><strong>Library:</strong> create reusable lessons and optional unit templates. <strong>Learning plans:</strong> organize a class learning unit, choose dates, and select classes. <strong>Teach:</strong> release the scheduled work to students.</p>
              <p className="mt-2 text-sm">Open a unit, choose one lesson or a week range, select the classes, and review the calendar before scheduling.</p>
            </section>
            <div className="ep-unit-grid">
              {catalog.units.map((row) => (
                <button
                  className="ep-card ep-unit-card"
                  key={row.id}
                  onClick={() => chooseUnit(row)}
                >
                  <strong>{row.title}</strong>
                  <span>
                    {
                      new Set([
                        ...row.activities.map((a) => a.plan_id),
                        ...(row.planning?.items || []).map((a) => a.plan_id),
                      ]).size
                    }{' '}
                    activities ·{' '}
                    {row.planning?.start_date
                      ? `${row.planning.weeks} weeks from ${pretty(row.planning.start_date, { month: 'short', day: 'numeric' })}`
                      : 'Dates not set'}
                  </span>
                  <span className="ep-back">Open unit planner →</span>
                </button>
              ))}
            </div>
            {!catalog.units.length && (
              <section className="ep-card">
                <h2>No planning units yet</h2>
                <p className="mt-2">
                  Create a unit for the final support project, then collect only
                  the activities that belong in it. You can connect your
                  existing Hook.
                </p>
              </section>
            )}
            <button
              className="ep-primary"
              onClick={() =>
                setSetup({
                  create: true,
                  title: '',
                  start: catalog.next_school_date || catalog.today,
                  weeks: 3,
                  membership: '',
                })
              }
            >
              Create planning unit
            </button>
          </>
        ) : (
          <>
            <nav className="ep-workspace-tabs" aria-label="Unit planning workspace">
              <button
                aria-pressed={plannerView === 'schedule'}
                onClick={() => navigateContext({ workspace: 'schedule' })}
              >
                Schedule lessons
                <small>Plan dates and classes</small>
              </button>
              <button
                aria-pressed={plannerView === 'agenda'}
                onClick={() => navigateContext({ workspace: 'agenda' })}
              >
                Daily agenda
                <small>Adjust one class and day</small>
              </button>
            </nav>
            {plannerView === 'schedule' ? <>
              <GuidedPlanning
              key={`guided:${unit.id}`}
              sectionId={Number(section)}
              unit={unit}
              plans={catalog.plans}
              sections={catalog.sections || []}
              defaultDate={chosenDate}
              busy={busy}
              setBusy={setBusy}
              onNotice={setNotice}
              onError={setError}
              onRefresh={refreshShared}
              />
              <div className="ep-planner-links">
                <Link className="ep-primary" to={`/v2/teacher/teach?section=${section}&date=${chosenDate}&unit=${unit.id}`}>Teach this class →</Link>
                <Link className="ep-secondary" to={`/v2/teacher/pathways?section=${unit.owner_section_id || section}`}>Unit details & assessments</Link>
              </div>
            </> : <>
            <div className="ep-context">
              <label>
                Unit
                <select
                  value={unit.id}
                  disabled={busy}
                  onChange={(event) => {
                    const row = catalog.units.find(
                      (u) => u.id === Number(event.target.value),
                    );
                    if (row) chooseUnit(row);
                  }}
                >
                  {catalog.units.map((row) => (
                    <option key={row.id} value={row.id}>
                      {row.title}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                School date
                <input
                  type="date"
                  value={chosenDate}
                  disabled={busy}
                  onChange={(event) => {
                    if (event.target.value)
                      navigateContext({ date: event.target.value });
                  }}
                />
              </label>
              <div className="ep-project">
                {unit.projects?.map((project) => (
                  <Link
                    key={project.hook_id}
                    to={`/v2/teacher/plan/hooks/${project.hook_id}?section=${section}`}
                  >
                    Project / Hook levels: {project.title}
                  </Link>
                ))}
              </div>
            </div>
            {!start ? (
              <div className="ep-notice">
                Set this unit’s start date to organize suggestions by week and
                day.{' '}
                <button
                  className="underline"
                  onClick={() =>
                    setSetup({
                      create: false,
                      title: unit.title,
                      start: chosenDate,
                      weeks,
                      membership: '',
                    })
                  }
                >
                  Set unit dates
                </button>
              </div>
            ) : (
              <nav className="ep-unit-weeks" aria-label="Unit weeks">
                {Array.from({ length: weeks }, (_, index) => (
                  <button
                    key={index}
                    disabled={busy}
                    aria-pressed={unitWeek === index + 1}
                    onClick={() =>
                      void navigateWeek(
                        index === 0 ? start : addDays(monday(start), index * 7),
                      )
                    }
                  >
                    Week {index + 1}
                  </button>
                ))}
              </nav>
            )}
            <nav className="ep-week" aria-label="School week">
              <button
                aria-label="Previous week"
                disabled={busy}
                onClick={() => void navigateWeek(addDays(chosenDate, -7))}
              >
                ←
              </button>
              <span>
                {inUnit ? `Unit week ${unitWeek}` : 'Outside unit dates'} ·{' '}
                {pretty(monday(chosenDate), { month: 'short', day: 'numeric' })}
              </span>
              {calendar
                .filter((row) => row.weekday < 4 || row.is_school_day)
                .map((row) => (
                  <button
                    key={row.date}
                    disabled={busy || !row.is_school_day}
                    aria-pressed={row.date === chosenDate}
                    aria-label={`${pretty(row.date, { weekday: 'short' })} ${pretty(row.date, { month: 'short', day: 'numeric' })}${!row.is_school_day ? ` ${row.closure_label || 'No school'}` : row.is_adjusted_school_friday ? ' Adjusted school day' : ''}`}
                    title={
                      row.closure_label ||
                      (row.is_school_day ? '' : 'No school')
                    }
                    onClick={() => navigateContext({ date: row.date })}
                  >
                    {pretty(row.date, { weekday: 'short' })}
                    <small>
                      {pretty(row.date, { month: 'short', day: 'numeric' })}
                    </small>
                    {!row.is_school_day && <small>No school</small>}
                    {row.is_adjusted_school_friday && <small>School day</small>}
                  </button>
                ))}
              <button
                aria-label="Next week"
                disabled={busy}
                onClick={() => void navigateWeek(addDays(chosenDate, 7))}
              >
                →
              </button>
            </nav>
            {calendarError && (
              <p className="ep-alert" role="alert">
                {calendarError}
              </p>
            )}
            {selectedSchoolDate && !instructionalDay && (
              <p className="ep-notice">
                {selectedSchoolDate.closure_label ||
                  'No class scheduled on this date.'}{' '}
                Choose an instructional date to add activities.
              </p>
            )}
            <h2 className="ep-agenda-heading">Review and adjust {catalog.section.name}’s agenda</h2>
            {activityGroups && <SharedStartingPlan key={`${context}:${activityGroups.id}`} unit={unit} section={section} date={chosenDate} day={day} groups={catalog.sections || []} activity={activityGroups} onClose={() => setActivityGroups(null)} onRefresh={refreshShared} />}
            <div className="ep-columns">
              <section
                className="ep-card ep-catalog"
                aria-label="Available activities"
              >
                <div className="ep-card-heading">
                  <div>
                    <h2>Find something for today</h2>
                    <p>
                      {source === 'original'
                        ? 'The suggested starting point—not a fixed calendar date.'
                        : source === 'history'
                          ? 'Previously scheduled in this class · last 90 days'
                          : scope === 'library'
                            ? 'Browse and collect activities for this unit'
                            : `${available.length} activities in this ${scope}`}
                    </p>
                  </div>
                </div>
                <nav className="ep-scopes" aria-label="Activity sources">
                  {(['original', 'category', 'history'] as const).map(
                    (value) => (
                      <button
                        key={value}
                        aria-pressed={source === value}
                        onClick={() => {
                          setSource(value);
                          setQuery('');
                          setTypeFilter('all');
                          setScope(value === 'category' ? 'library' : 'unit');
                          if (value === 'category') setProjectFilter('all');
                        }}
                      >
                        {
                          {
                            original: 'Original lesson',
                            category: 'By category',
                            history: 'Bring back',
                          }[value]
                        }
                      </button>
                    ),
                  )}
                </nav>
                {source === 'original' && (
                  <div className="ep-original">
                    {originalMeeting && <button className="ep-secondary" onClick={() => setPreviewLesson(true)}>Preview complete lesson</button>}
                    {previewLesson && originalMeeting && <LessonSequencePreview title={originalMeeting.title} planIds={originalMeeting.items.map(item => item.plan_id)} onClose={() => setPreviewLesson(false)} />}
                    <label>
                      Suggested lesson
                      <select
                        value={
                          originalMeeting
                            ? `${originalMeeting.project_key}:${originalMeeting.key}`
                            : ''
                        }
                        onChange={(event) => setMeeting(event.target.value)}
                      >
                        <option value="">
                          Choose an imported lesson group
                        </option>
                        {originalMeetings.map((row) => (
                          <option
                            key={`${row.project_key}:${row.key}`}
                            value={`${row.project_key}:${row.key}`}
                          >
                            {row.title}
                          </option>
                        ))}
                      </select>
                    </label>
                    <p className="ep-dialog-note">
                      {originalMeeting
                        ? 'Original order is preserved. Your daily changes do not edit this lesson. Optional support and extensions are added individually.'
                        : 'No lesson group selected. Showing your unit collection below; it is not an authored lesson sequence. Import a complete meeting recipe to see its original grouping.'}
                    </p>
                    {originalMeeting && (
                      <button
                        className="ep-secondary"
                        disabled={
                          !missingCore.length ||
                          busy ||
                          !instructionalDay ||
                          !day?.agenda_enabled
                        }
                        onClick={() =>
                          void command(
                            'agenda_add_meeting',
                            {
                              project_key: originalMeeting.project_key,
                              meeting_key: originalMeeting.key,
                              core_only: true,
                              skip_existing: true,
                              whole_class: true,
                              student_ids: [],
                            },
                            'Missing core activities added. Original lesson unchanged.',
                          )
                        }
                      >
                        {missingCore.length
                          ? 'Add missing core activities'
                          : 'Core activities already added'}
                      </button>
                    )}
                  </div>
                )}
                {source === 'category' && (
                  <>
                    <details className="ep-collection-controls">
                      <summary>Saved unit suggestions</summary>
                      <nav className="ep-scopes" aria-label="Activity scope">
                        {(['day', 'week', 'unit', 'library'] as Scope[]).map(
                          (value) => (
                            <button
                              key={value}
                              aria-pressed={scope === value}
                              onClick={() => {
                                setScope(value);
                                setQuery('');
                              }}
                            >
                              {
                                {
                                  day: 'This day',
                                  week: 'This week',
                                  unit: 'Entire unit',
                                  library: 'Browse library',
                                }[value]
                              }
                            </button>
                          ),
                        )}
                      </nav>
                    </details>
                    <div className="ep-filters">
                      <input
                        aria-label="Search activities"
                        placeholder="Search activities"
                        value={query}
                        onChange={(event) => setQuery(event.target.value)}
                      />
                      <select
                        aria-label="Activity type"
                        value={typeFilter}
                        onChange={(event) => setTypeFilter(event.target.value)}
                      >
                        <option value="all">All categories</option>
                        {Object.entries(labels).map(([key, label]) => (
                          <option key={key} value={key}>
                            {label}
                          </option>
                        ))}
                      </select>
                    </div>
                    {scope === 'library' && (
                      <label className="ep-library-source">
                        Library source
                        <select
                          value={projectFilter}
                          onChange={(event) =>
                            setProjectFilter(event.target.value)
                          }
                        >
                          <option value="connected">Connected projects</option>
                          <option value="all">All class content</option>
                          <option value="unit">This unit</option>
                          {catalog.memberships?.map((row) => (
                            <option key={row.id} value={row.hook_id}>
                              {row.title}
                            </option>
                          ))}
                        </select>
                      </label>
                    )}
                  </>
                )}
                {source === 'history' && (
                  <div className="ep-history">
                    <p className="ep-dialog-note">
                      Add a fresh review for this day. Previous work stays
                      intact. Added for everyone; use Adjust for a smaller
                      group.
                    </p>
                    <input
                      aria-label="Search class history"
                      placeholder="Find yesterday’s explanation or last week’s lab"
                      value={query}
                      onChange={(event) => setQuery(event.target.value)}
                    />
                    {historyLoading && (
                      <p role="status">Loading class history…</p>
                    )}
                    {historyError && <p role="alert">{historyError}</p>}
                    <div className="ep-activity-list">
                      {history
                        .map((item, index) => ({ item, index }))
                        .filter(({ item }) =>
                          item.title
                            .toLowerCase()
                            .includes(query.toLowerCase()),
                        )
                        .map(({ item, index }) => (
                          <div className="ep-activity" key={item.id}>
                            <button
                              className="ep-drag"
                              aria-label={`Drag previous ${item.title} to daily plan`}
                              disabled={
                                busy ||
                                !instructionalDay ||
                                !day?.agenda_enabled
                              }
                              draggable={
                                !busy &&
                                instructionalDay &&
                                !!day?.agenda_enabled
                              }
                              onDragStart={(event) =>
                                startDrag(event, 'history', index)
                              }
                              onDragEnd={() => {
                                setDrag(null);
                                setDropIndex(null);
                              }}
                            >
                              ⠿
                            </button>
                            <div className="ep-activity-title">
                              <strong>{item.title}</strong>
                              <p>
                                {pretty(item.date, {
                                  month: 'short',
                                  day: 'numeric',
                                })}{' '}
                                · {item.status}
                                {item.minutes ? ` · ${item.minutes} min` : ''}
                              </p>
                            </div>
                            <button
                              className="ep-add"
                              disabled={
                                busy ||
                                !instructionalDay ||
                                !day?.agenda_enabled
                              }
                              onClick={() => beginReuse(item)}
                              aria-label={`Bring back ${item.title} from ${item.date}`}
                            >
                              + Review
                            </button>
                          </div>
                        ))}
                      {!historyLoading && !historyError && !history.length && (
                        <p>
                          No reusable activities recorded for this class in the
                          previous 90 days.
                        </p>
                      )}
                    </div>
                  </div>
                )}
                {source !== 'history' && (
                  <>
                    <div
                      className="ep-activity-list"
                      tabIndex={0}
                      aria-label="Activity list"
                    >
                      {displayedPlans.map((plan) => (
                        <div className="ep-activity" key={plan.id}>
                          <button
                            className="ep-drag"
                            aria-label={`Drag ${plan.title} to daily plan`}
                            disabled={
                              busy || !instructionalDay || !day?.agenda_enabled
                            }
                            draggable={
                              !busy && instructionalDay && !!day?.agenda_enabled
                            }
                            onDragStart={(event) =>
                              startDrag(event, 'plan', plan.id)
                            }
                            onDragEnd={() => {
                              setDrag(null);
                              setDropIndex(null);
                            }}
                            title="Drag to the daily plan, or use Add"
                          >
                            ⠿
                          </button>
                          <div className="ep-activity-title">
                            <strong>{plan.title}</strong>
                            <p>
                              {purpose(plan)}
                              {plan.hook_level
                                ? ` · ${plan.hook_level.toUpperCase()}`
                                : ''}
                            </p>
                            {source === 'original' && originalMeeting && (
                              <p className="ep-provenance">
                                {optionalItem(
                                  originalMeeting.items.find(
                                    (item) => item.plan_id === plan.id,
                                  )!,
                                )
                                  ? 'Optional support / extension'
                                  : 'Originally planned'}{' '}
                                · {originalMeeting.title}
                              </p>
                            )}
                            {source === 'category' && (
                              <p className="ep-provenance">
                                {catalog.meetings.find((row) =>
                                  row.items.some(
                                    (item) => item.plan_id === plan.id,
                                  ),
                                )?.title ||
                                  catalog.memberships.find(
                                    (row) => row.hook_id === plan.hook_id,
                                  )?.title ||
                                  'Reusable activity · no lesson group linked'}
                              </p>
                            )}
                            <button
                              className="ep-text-button"
                              disabled={busy || !start}
                              onClick={() => {
                                setError('');
                                setSuggestion({
                                  plan,
                                  where:
                                    inUnit && instructionalDay ? 'day' : 'unit',
                                });
                              }}
                            >
                              {unitPlanIds.has(plan.id)
                                ? 'Organize in unit'
                                : 'Collect in unit (optional)'}
                            </button>
                          </div>
                          <button
                            className="ep-add"
                            disabled={
                              busy || !instructionalDay || !day?.agenda_enabled
                            }
                            onClick={() => beginAdd(plan)}
                            aria-label={`Add ${plan.title} to ${pretty(chosenDate, { weekday: 'long', month: 'long', day: 'numeric' })}`}
                          >
                            + Add to day
                          </button>
                        </div>
                      ))}
                      {!displayedPlans.length && (
                        <div className="ep-empty">
                          <p>
                            {scope === 'day' || scope === 'week'
                              ? 'No activity suggestions saved here yet. Organize activities from Entire unit for this week or day.'
                              : scope === 'unit'
                                ? 'This unit has no collected activities yet. Browse the library to add some.'
                                : 'No matching imported activities.'}
                          </p>
                          <button
                            className="ep-text-button"
                            onClick={() => {
                              setScope(
                                scope === 'day' || scope === 'week'
                                  ? 'unit'
                                  : 'library',
                              );
                              setQuery('');
                              setTypeFilter('all');
                              setSource('category');
                            }}
                          >
                            {scope === 'day' || scope === 'week'
                              ? 'Show entire unit'
                              : 'Browse library'}
                          </button>
                        </div>
                      )}
                    </div>
                  </>
                )}
                <details className="ep-import">
                  <summary>Missing an activity? Import from repository</summary>
                  <p>
                    Import into a connected project, then return here and
                    refresh activities. You can add directly to the day;
                    collecting in a unit is optional.
                  </p>
                  {(catalog.memberships || []).map((row) => (
                    <Link key={row.id} to={repositoryUrl(row.hook_id)}>
                      Import for {row.title} →
                    </Link>
                  ))}
                  <Link to={`/v2/teacher/plan?section=${section}`}>
                    Open Library
                  </Link>
                </details>
                {source === 'category' && !!visibleMeetings.length && (
                  <div className="ep-meeting">
                    <label>
                      Meeting recipe
                      <select
                        value={meeting}
                        onChange={(event) => setMeeting(event.target.value)}
                      >
                        <option value="">Choose a meeting</option>
                        {visibleMeetings.map((row) => (
                          <option
                            key={`${row.project_key}:${row.key}`}
                            value={`${row.project_key}:${row.key}`}
                          >
                            {row.title}
                          </option>
                        ))}
                      </select>
                    </label>
                    {scope === 'library' ? (
                      <button
                        className="ep-secondary"
                        disabled={!selectedMeeting || busy || !start}
                        onClick={collectMeeting}
                      >
                        Collect in unit
                      </button>
                    ) : (
                      <button
                        className="ep-secondary"
                        disabled={
                          !selectedMeeting ||
                          busy ||
                          !instructionalDay ||
                          !day?.agenda_enabled
                        }
                        onClick={() =>
                          selectedMeeting &&
                          void command(
                            'agenda_add_meeting',
                            {
                              project_key: selectedMeeting.project_key,
                              meeting_key: selectedMeeting.key,
                              whole_class: true,
                              student_ids: [],
                            },
                            'Meeting activities added in their authored order.',
                          )
                        }
                      >
                        Add meeting
                      </button>
                    )}
                  </div>
                )}
              </section>
              <section className="ep-card ep-daily" aria-label="Daily plan">
                <div className="ep-card-heading">
                  <div>
                    <h2>{pretty(chosenDate, { weekday: 'long' })}’s plan</h2>
                    <p>
                      {catalog.section.name} ·{' '}
                      {pretty(chosenDate, {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      })}
                    </p>
                  </div>
                  <span className="ep-saved">
                    {busy ? 'Saving…' : 'Saved as you plan'}
                  </span>
                  <button
                    className="ep-primary"
                    disabled={busy || !instructionalDay || !day?.agenda_enabled}
                    onClick={beginQuick}
                  >
                    + Quick add
                  </button>
                </div>
                <p className="ep-dialog-note">
                  Changes affect only {catalog.section.name} and this date.
                  Activities from other units remain in this day. The original
                  lesson stays unchanged.
                </p>
                {!day ? (
                  <p role="status">Loading this day…</p>
                ) : !day.agenda_enabled ? (
                  <div className="ep-empty">
                    <h3>This date uses the original classroom plan</h3>
                    <p>
                      Customize this date to add, reorder, or adjust activities.
                      Existing classroom-plan activities will be carried into the
                      daily agenda.
                    </p>
                    <button
                      className="ep-primary"
                      disabled={busy || !instructionalDay}
                      onClick={() =>
                        void command(
                          'agenda_enable',
                          {},
                          'This day is ready to customize.',
                        )
                      }
                    >
                      Customize this day
                    </button>
                    <Link
                      className="ep-secondary"
                      to={`/v2/teacher/teach?section=${section}&date=${chosenDate}`}
                    >
                      Open this day in Teach
                    </Link>
                  </div>
                ) : (
                  <>
                    <label className="ep-period">
                      Planning target (this view)
                      <input
                        type="number"
                        min={1}
                        max={480}
                        value={periodMinutes}
                        onChange={(event) => {
                          const value = Number(event.target.value);
                          if (
                            Number.isInteger(value) &&
                            value > 0 &&
                            value <= 480
                          )
                            setPeriodMinutes(value);
                        }}
                      />
                    </label>
                    <div className="ep-time">
                      <strong>
                        {totalMinutes} / {periodMinutes} min planned
                      </strong>
                      <span>
                        {totalMinutes > periodMinutes
                          ? `${totalMinutes - periodMinutes} min over`
                          : `${periodMinutes - totalMinutes} min available`}
                      </span>
                    </div>
                    <div
                      className="ep-track"
                      role="progressbar"
                      aria-label="Minutes planned"
                      aria-valuenow={Math.min(totalMinutes, periodMinutes)}
                      aria-valuemin={0}
                      aria-valuemax={periodMinutes}
                    >
                      <span
                        style={{
                          width: `${Math.min(100, (totalMinutes / periodMinutes) * 100)}%`,
                        }}
                      />
                    </div>
                    {!!missingMinutes && (
                      <p className="ep-dialog-note">
                        {missingMinutes} activities still need a time estimate.
                      </p>
                    )}
                    <ol
                      className="ep-plan-list"
                      aria-label="Ordered daily activities"
                    >
                      {agenda.map((item, index) => (
                        <li
                          key={item.id}
                          className={`ep-plan-item ${dropIndex === index ? 'ep-drop-target' : ''}`}
                          {...dropProps(index)}
                        >
                          {item.lesson_group?.step_index === 1 && (
                            <div className="ep-lesson-group-heading">
                              <strong>{item.lesson_group.title}</strong>
                              <span>Customized for this day · Library source unchanged</span>
                            </div>
                          )}
                          <button
                            className="ep-drag"
                            aria-label={`Drag ${item.title} to reorder`}
                            disabled={busy || !instructionalDay}
                            draggable={!busy && instructionalDay}
                            onDragStart={(event) =>
                              startDrag(event, 'agenda', item.id)
                            }
                            onDragEnd={() => {
                              setDrag(null);
                              setDropIndex(null);
                            }}
                          >
                            ⠿
                          </button>
                          <div>
                            <strong>
                              {index + 1}. {item.title}
                            </strong>
                            <p>
                              {item.lesson_group?.role ? `${labels[item.lesson_group.role] || item.lesson_group.role} · ` : ''}
                              {item.pacing === 'teacher'
                                ? 'Teacher-led'
                                : 'At students’ pace'}{' '}
                              ·{' '}
                              {item.whole_class
                                ? 'Everyone'
                                : `${item.student_ids.length} students`}{' '}
                              · {item.status}
                              {item.minutes
                                ? ` · ${item.minutes} min`
                                : ' · Time not set'}
                            </p>
                          </div>
                          <div className="ep-move">
                            {!!item.lesson_outline?.length && !item.lesson_group?.key && (
                              <button className="ep-adjust" onClick={() => setOpenLessonIds((current) => current.includes(item.id) ? current.filter((id) => id !== item.id) : [...current, item.id])}>
                                {openLessonIds.includes(item.id) ? 'Hide steps' : 'Show steps'}
                              </button>
                            )}
                            {item.lesson_customizable && !item.lesson_group?.key && item.status === 'planned' && (
                              <button className="ep-adjust" disabled={busy} onClick={() => void command('agenda_expand_lesson', { item_id: item.id }, `${item.title} expanded into adjustable activities for this day. The Library lesson is unchanged.`)}>
                                Customize today
                              </button>
                            )}
                            {item.whole_class && ['lesson', 'quick', 'work'].includes(item.kind) && <button className="ep-adjust" disabled={busy} onClick={() => setActivityGroups(item)}>Class groups</button>}
                            {item.status === 'planned' && (
                              <>
                                <button
                                  className="ep-adjust"
                                  disabled={busy || !instructionalDay}
                                  onClick={() => beginAdjust(item)}
                                >
                                  Adjust
                                </button>
                                <button
                                  disabled={busy || !instructionalDay}
                                  aria-label={`Remove ${item.title} from this day`}
                                  onClick={() =>
                                    void command(
                                      'agenda_skip',
                                      { item_id: item.id },
                                      'Removed from this day. The source activity is unchanged.',
                                    )
                                  }
                                >
                                  Remove
                                </button>
                              </>
                            )}
                            <button
                              aria-label={`Move ${item.title} up`}
                              disabled={
                                busy || !instructionalDay || index === 0
                              }
                              onClick={() =>
                                reorder(item.id, agenda[index - 1].id)
                              }
                            >
                              ↑
                            </button>
                            <button
                              aria-label={`Move ${item.title} down`}
                              disabled={
                                busy ||
                                !instructionalDay ||
                                index === agenda.length - 1
                              }
                              onClick={() =>
                                reorder(item.id, agenda[index + 2]?.id)
                              }
                            >
                              ↓
                            </button>
                          </div>
                          {openLessonIds.includes(item.id) && !!item.lesson_outline?.length && (
                            <div className="ep-lesson-outline">
                              <p>Preview the authored sequence. Customize only when you need to insert, retime, or target activities separately.</p>
                              <ol>
                                {item.lesson_outline.map((step, stepIndex) => (
                                  <li key={step.id}>
                                    <span>{stepIndex + 1}</span>
                                    <div><strong>{step.title}</strong><small>{labels[step.step_kind] || step.step_kind}{step.minutes ? ` · ${step.minutes} min` : ''}</small></div>
                                  </li>
                                ))}
                              </ol>
                              {!item.lesson_customizable && <p>This lesson stays together because its Studio, evidence, artifact, or cross-step response behavior must remain intact.</p>}
                            </div>
                          )}
                        </li>
                      ))}
                    </ol>
                    <div
                      className={`ep-dropzone ${dropIndex === agenda.length ? 'ep-drop-target' : ''}`}
                      {...dropProps(agenda.length)}
                    >
                      Drag an activity here
                      <p>Or use + Add. Drag within the plan to reorder.</p>
                    </div>
                  </>
                )}
                <div className="ep-plan-footer">
                  <span>Activities open for students from Teach.</span>
                  <Link
                    className="ep-secondary"
                    to={`/v2/teacher/teach?section=${section}&date=${chosenDate}`}
                  >
                    Review in Teach
                  </Link>
                </div>
              </section>
            </div>
            </>}
          </>
        )}
        <Dialog
          open={!!(draft || setup || suggestion)}
          onClose={closeDialogs}
          className="ep-dialog-wrap"
        >
          <div className="ep-overlay">
            <Dialog.Panel className="ep-dialog">
              <div className="ep-dialog-head">
                <Dialog.Title as="h2">{dialogTitle}</Dialog.Title>
                <button
                  aria-label="Close activity options"
                  disabled={busy}
                  onClick={closeDialogs}
                >
                  ×
                </button>
              </div>
              {error && (
                <p role="alert" className="ep-alert">
                  {error}
                </p>
              )}
              {setup && (
                <div className="ep-dialog-fields">
                  {setup.create && (
                    <>
                      <label>
                        Unit title
                        <input
                          value={setup.title}
                          maxLength={200}
                          onChange={(event) =>
                            setSetup({ ...setup, title: event.target.value })
                          }
                        />
                      </label>
                      <label>
                        Connect existing project (optional)
                        <select
                          value={setup.membership}
                          onChange={(event) =>
                            setSetup({
                              ...setup,
                              membership: event.target.value,
                            })
                          }
                        >
                          <option value="">No project connection</option>
                          {catalog?.memberships?.map((row) => (
                            <option key={row.id} value={row.id}>
                              {row.title}
                            </option>
                          ))}
                        </select>
                      </label>
                    </>
                  )}
                  <label>
                    Unit start date
                    <input
                      type="date"
                      value={setup.start}
                      onChange={(event) =>
                        setSetup({ ...setup, start: event.target.value })
                      }
                    />
                  </label>
                  <label>
                    Number of weeks
                    <input
                      type="number"
                      min={1}
                      max={52}
                      value={setup.weeks}
                      onChange={(event) =>
                        setSetup({
                          ...setup,
                          weeks: Number(event.target.value),
                        })
                      }
                    />
                  </label>
                  <p>
                    Unit weeks follow the Monday–Sunday calendar containing the
                    start date. Changing dates moves suggestions; saved daily
                    agendas stay on their original dates.
                  </p>
                  <button
                    className="ep-primary"
                    disabled={
                      busy ||
                      !setup.start ||
                      (setup.create && !setup.title.trim()) ||
                      !Number.isInteger(setup.weeks) ||
                      setup.weeks < 1 ||
                      setup.weeks > 52
                    }
                    onClick={saveSetup}
                  >
                    {busy
                      ? 'Saving…'
                      : setup.create
                        ? 'Create unit'
                        : 'Save unit dates'}
                  </button>
                </div>
              )}
              {suggestion && (
                <div className="ep-dialog-fields">
                  <p>
                    Save this activity in {unit?.title}. This collects a
                    reusable suggestion without assigning student work.
                  </p>
                  <label>
                    Suggest for
                    <select
                      value={suggestion.where}
                      onChange={(event) =>
                        setSuggestion({
                          ...suggestion,
                          where: event.target.value as 'unit' | 'week' | 'day',
                        })
                      }
                    >
                      <option value="unit">Entire unit</option>
                      <option value="week" disabled={!inUnit}>
                        Unit week {unitWeek}
                      </option>
                      <option
                        value="day"
                        disabled={!inUnit || !instructionalDay}
                      >
                        {pretty(chosenDate, {
                          weekday: 'long',
                          month: 'short',
                          day: 'numeric',
                        })}
                      </option>
                    </select>
                  </label>
                  <p>
                    This replaces the activity’s previous suggestion location in
                    this unit.
                  </p>
                  <button
                    className="ep-primary"
                    disabled={busy}
                    onClick={() => saveSuggestion()}
                  >
                    Save in unit
                  </button>
                  {planning?.items?.some(
                    (item) => item.plan_id === suggestion.plan.id,
                  ) && (
                    <button
                      className="ep-secondary"
                      disabled={busy}
                      onClick={() => saveSuggestion(true)}
                    >
                      Remove planning suggestion
                    </button>
                  )}
                </div>
              )}
              {draft && (
                <>
                  <div className="ep-dialog-fields">
                    {(draft.kind === 'quick' || draft.kind === 'work') && (
                      <>
                        <label>
                          Activity title
                          <input
                            maxLength={200}
                            value={draft.title}
                            onChange={(event) =>
                              setDraft({ ...draft, title: event.target.value })
                            }
                            placeholder="Gimkit · hardware review"
                          />
                        </label>
                        {draft.kind === 'quick' && (
                          <label>
                            Link (optional)
                            <input
                              type="url"
                              maxLength={1000}
                              value={draft.url}
                              onChange={(event) =>
                                setDraft({ ...draft, url: event.target.value })
                              }
                              placeholder="https://…"
                            />
                          </label>
                        )}
                        <label>
                          Directions (optional)
                          <textarea
                            maxLength={10000}
                            value={draft.directions}
                            onChange={(event) =>
                              setDraft({
                                ...draft,
                                directions: event.target.value,
                              })
                            }
                          />
                        </label>
                        <p className="ep-dialog-note">
                          For this class and day only. No library activity or
                          unit filing is created.
                        </p>
                      </>
                    )}
                    <label>
                      Delivery
                      <select
                        value={draft.pacing}
                        onChange={(event) =>
                          setDraft({
                            ...draft,
                            pacing: event.target.value as Draft['pacing'],
                          })
                        }
                      >
                        <option value="teacher">Teacher-led</option>
                        <option value="independent">At students’ pace</option>
                      </select>
                    </label>
                    <label>
                      Estimated minutes (optional)
                      <input
                        type="number"
                        min={1}
                        max={480}
                        value={draft.minutes}
                        onChange={(event) =>
                          setDraft({ ...draft, minutes: event.target.value })
                        }
                      />
                    </label>
                    <label>
                      Audience
                      <select
                        value={draft.wholeClass ? 'all' : 'selected'}
                        onChange={(event) =>
                          setDraft({
                            ...draft,
                            wholeClass: event.target.value === 'all',
                            studentIds: [],
                          })
                        }
                      >
                        <option value="all">Everyone</option>
                        <option value="selected">Selected students</option>
                      </select>
                    </label>
                    {!draft.wholeClass && (
                      <fieldset>
                        <legend>Choose students</legend>
                        <div className="ep-students">
                          {day?.students.map((student) => (
                            <label key={student.id}>
                              <input
                                type="checkbox"
                                checked={draft.studentIds.includes(student.id)}
                                onChange={(event) =>
                                  setDraft({
                                    ...draft,
                                    studentIds: event.target.checked
                                      ? [...draft.studentIds, student.id]
                                      : draft.studentIds.filter(
                                          (id) => id !== student.id,
                                        ),
                                  })
                                }
                              />{' '}
                              {student.name}
                            </label>
                          ))}
                        </div>
                      </fieldset>
                    )}
                  </div>
                  <p className="ep-dialog-note">
                    Save to{' '}
                    {pretty(chosenDate, {
                      weekday: 'long',
                      month: 'short',
                      day: 'numeric',
                    })}
                    {draft.before ? ' at the chosen position' : ''}. This does
                    not open student work.
                  </p>
                  <div className="ep-dialog-actions">
                    <button
                      className="ep-secondary"
                      disabled={busy}
                      onClick={closeDialogs}
                    >
                      Cancel
                    </button>
                    <button
                      className="ep-primary"
                      disabled={
                        busy ||
                        ((draft.kind === 'quick' || draft.kind === 'work') &&
                          !draft.title.trim()) ||
                        (!!draft.minutes.trim() &&
                          (!Number.isInteger(Number(draft.minutes)) ||
                            Number(draft.minutes) < 1 ||
                            Number(draft.minutes) > 480)) ||
                        (!draft.wholeClass && !draft.studentIds.length)
                      }
                      onClick={saveDraft}
                    >
                      {busy
                        ? 'Saving…'
                        : draft.itemId
                          ? 'Save changes'
                          : 'Add to day'}
                    </button>
                  </div>
                </>
              )}
            </Dialog.Panel>
          </div>
        </Dialog>
      </div>
    </AppShell>
  );
}
